import posthog from "posthog-js";
import config from "../helper/config";
import { initDebugHarness, recordEvent } from "./debug";
import { EVENTS } from "./events";
import { initControlTracking } from "./controlTracking";

let initialized = false;

// Built once at module level. "$snapshot" is session replay: the recorder emits it via
// posthog.capture(), so it passes through before_send like every other event — dropping
// it would silently kill the always-on impersonation recordings (identity.js).
const ALLOWED_EVENTS = new Set([
  "$autocapture",
  "$identify",
  "$groupidentify",
  "$set",
  "$snapshot",
  ...Object.values(EVENTS),
]);

// Controls whose clicks are auto-captured. Plain text inputs, table rows, menu items and
// generic divs are intentionally absent. Disabled controls never receive clicks.
const CLICKABLE_SELECTORS = [
  "button", // antd Button, Switch (<button role="switch">), icon buttons
  "a", // links, antd <Button href>
  "input[type=button]",
  "input[type=submit]",
  "[role=button]",
  "[role=tab]",
  ".ant-tabs-tab", // tab header
  ".ant-collapse-header", // collapse/accordion header
  ".ant-select", // Select (opening it)
  ".ant-select-item-option", // Select (choosing an option; dropdown is portaled)
];

function allowlistEvents(cr) {
  return cr && ALLOWED_EVENTS.has(cr.event) ? cr : null;
}

// Machine-traffic routes hit by external builder widgets on customer sites — never track.
// Mirrors the <TrackingHandler> routes registered in App.js. Most sit under /api/, but
// "/ProductTracking" (RingBuilder) does not, so it needs an explicit exact-match entry —
// a prefix check on "/api/" alone would silently start tracking it as human traffic.
const EXCLUDED_PATH_PREFIXES = ["/api/"];
const EXCLUDED_EXACT_PATHS = ["/ProductTracking"];

export function isExcludedPath(pathname) {
  const p = pathname || window.location.pathname || "";
  return (
    EXCLUDED_PATH_PREFIXES.some((prefix) => p.startsWith(prefix)) ||
    EXCLUDED_EXACT_PATHS.includes(p)
  );
}

export function initAnalytics() {
  if (initialized) return;
  if (!config.PostHogKey) return; // empty key in this block -> no-op
  if (isExcludedPath()) return; // /api/**/Tracking = builder widgets, not humans

  posthog.init(config.PostHogKey, {
    api_host: config.PostHogHost,
    ui_host: config.PostHogUiHost,

    // Per-tab identity store. Impersonation opens a second tab via window.open while the
    // admin tab stays live; shared localStorage would let the two identities overwrite
    // each other. This must stay sessionStorage.
    persistence: "sessionStorage",

    // Narrow capture: no pageviews/pageleaves; the only automatic capture is clicks on
    // interactive controls (CLICKABLE_SELECTORS). Everything else must come from explicit
    // track() calls.
    capture_pageview: false,
    capture_pageleave: false,
    capture_dead_clicks: false,
    rageclick: false,
    capture_heatmaps: false,
    capture_performance: false,
    capture_exceptions: false,
    autocapture: {
      dom_event_allowlist: ["click"],
      // Deliberately no element_allowlist: it is tag-only and ANDed with this selector
      // list, so it could not express role="button" or Ant Design classes. A click is
      // captured when the clicked element or any ancestor matches one of these.
      css_selector_allowlist: CLICKABLE_SELECTORS,
      // Checkbox/radio/switch report their NEW state via controlTracking.js, so skip them
      // here to avoid two events per action. Setting this replaces the SDK default
      // ignorelist, so the two default entries are repeated.
      css_selector_ignorelist: [
        ".ph-no-autocapture",
        "[data-ph-no-autocapture]",
        "[role=switch]",
        "input[type=checkbox]",
        "input[type=radio]",
      ],
    },

    disable_session_recording: true, // started explicitly, see identity.js
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]",
      blockSelector: "[data-ph-block]",
      recordCrossOriginIframes: false, // builders are cross-origin; never record them
      sampleRate: 0.3,
      minimumDurationMilliseconds: 3000,
    },

    loaded: (ph) => {
      if (config.Environment !== "production") ph.debug();
    },

    // 1) allowlistEvents drops anything not explicitly allowed, even if a feature is later
    //    switched on in the PostHog project settings.
    // 2) Non-production only: feeds window.__phDebug's recentEvents() ring buffer (task
    //    #1749 two-tab QA gate). Runs after the filter so it only sees events that are
    //    actually sent, and never mutates the event.
    before_send:
      config.Environment !== "production"
        ? [
            allowlistEvents,
            (cr) => {
              if (cr) recordEvent(posthog.get_distinct_id(), cr.event, cr.properties);
              return cr;
            },
          ]
        : [allowlistEvents],
  });

  // window.open() clones the opener's sessionStorage, so a fresh impersonation tab boots
  // holding the ADMIN's distinct_id. window.opener is truthy ONLY in the opened tab —
  // that is what keeps the admin tab (which also sees IMPERSONATE_HANDOFF in localStorage
  // for a short window) from resetting itself.
  try {
    if (window.opener && window.localStorage.getItem("IMPERSONATE_HANDOFF")) {
      posthog.reset(true); // true = regenerate device_id too
    }
  } catch (e) {
    // storage blocked — ignore
  }

  if (config.Environment !== "production") {
    try {
      window.sessionStorage.setItem("__ph_probe", "1");
      window.sessionStorage.removeItem("__ph_probe");
    } catch (e) {
      console.warn(
        "[analytics] sessionStorage unavailable — PostHog will fall back to " +
          "localStorage and impersonation identity WILL collide across tabs."
      );
    }
  }

  posthog.register({
    app_version: process.env.REACT_APP_VERSION || "0.1.0",
    environment: config.Environment,
  });

  initControlTracking((event, props) => posthog.capture(event, props));

  initDebugHarness(posthog);

  initialized = true;
}

export default posthog;
